import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Checkbox,
  LinearProgress,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";

import {
  formatPercent,
  formatMt,
  getMetricTone,
  getSafeNumber,
  getSellerVmId,
  getToneColor,
} from "../makeData";
import { DEFAULT_SELLER_CARD_THRESHOLDS } from "../../Dashboard/config";
import { pascalCase } from "utils";

const thresholds = DEFAULT_SELLER_CARD_THRESHOLDS;

const Metric = React.memo(({ value, label, color }) => (
  <Box>
    <Typography sx={{ color, fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>
      {value}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: "text.secondary",
        fontSize: 10,
        lineHeight: 1.1,
        fontWeight: 500,
      }}
    >
      {label}
    </Typography>
  </Box>
));

const SellerCard = ({ seller = {}, checked = false, onChange }) => {
  const theme = useTheme();
  const { vmId, fgReadiness, fgReadinessTone } = useMemo(() => {
    return {
      vmId: getSellerVmId(seller),
      fgReadiness: getSafeNumber(seller.fgReadiness),
      fgReadinessTone: getMetricTone(
        seller.fgReadiness,
        thresholds.fgReadiness,
      ),
    };
  }, [seller]);

  const handleCardClick = useCallback(() => {
    onChange(vmId);
  }, [onChange, vmId]);

  const handleCheckboxClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const handleCheckboxChange = useCallback(() => {
    onChange(vmId);
  }, [onChange, vmId]);

  return (
    <Paper
      elevation={-1}
      onClick={handleCardClick}
      sx={{
        p: 3,
        pt: 3,
        pb: 3,
        mb: 2,
        position: "relative",
        border: "1px solid",
        borderColor: theme.palette.grey[300],
        borderRadius: 1,
        overflow: "hidden",
        backgroundColor: checked
          ? theme.palette.info.lightBlueBackround
          : theme.palette.common.white,
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "3px",
          backgroundColor: checked
            ? theme.palette.primary.main
            : theme.palette.grey[400],
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
        <Checkbox
          checked={checked}
          onClick={handleCheckboxClick}
          onChange={handleCheckboxChange}
          size="small"
          sx={{ p: 0 }}
        />

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: 14,
              mt: 0.25,
              fontWeight: 700,
              lineHeight: 1.25,
            }}
          >
            {pascalCase(seller.companyName)}
          </Typography>
          <Typography
            sx={{
              color: theme.palette.grey[1000],
              fontSize: 12,
              lineHeight: 1.5,
              mt: 0.25,
            }}
          >
            {vmId}
          </Typography>
        </Box>

        <Box sx={{ textAlign: "right", mt: -1 }}>
          <Metric
            value={seller.openOrders}
            label="OPEN ORDERS"
            color={theme.palette.text.primary}
          />
        </Box>
      </Box>

      <Box sx={{ mt: 1.25 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.grey[1000],
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            FG READINESS
          </Typography>
          <Typography
            sx={{
              color: getToneColor(theme, fgReadinessTone),
              fontSize: 13,
              fontWeight: 700,
              minWidth: 28,
              textAlign: "right",
            }}
          >
            {formatPercent(seller.fgReadiness)}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(fgReadiness, 100)}
          sx={{
            flex: 1,
            height: 4,
            borderRadius: 4,
            backgroundColor: theme.palette.grey[300],
            "& .MuiLinearProgress-bar": {
              backgroundColor: getToneColor(theme, fgReadinessTone),
            },
          }}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          rowGap: 2,
          mt: 2,
        }}
      >
        <Metric
          value={formatMt(seller.tonnageDue)}
          label="TONNAGE DUE"
          color={theme.palette.text.primary}
        />
        <Metric
          value={formatMt(seller.orderedQty)}
          label="ORDERED"
          color={theme.palette.text.primary}
        />
        <Metric
          value={formatMt(seller.fgReadyQty)}
          label="FG READY"
          color={theme.palette.text.primary}
        />
        <Metric
          value={formatMt(seller.invoicedQty)}
          label="INVOICED"
          color={theme.palette.text.primary}
        />
      </Box>
    </Paper>
  );
};

SellerCard.propTypes = {
  seller: PropTypes.shape({
    vmId: PropTypes.string,
    companyName: PropTypes.string,
    fgReadiness: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    openOrders: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    tonnageDue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    orderedQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    fgReadyQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    invoicedQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

export default React.memo(SellerCard);
